import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";

export type badgeVariantProps =
  | "pink"
  | "skyblue"
  | "green"
  | "yellow"
  | "lime"
  | "violet"
  | "purple"
  | "available"
  | "positive"
  | "caution"
  | "urgent"
  | "primary"
  | "red"
  | "none"
  | "neutral"
  | "secondary"
  | null
  | undefined;

const badgeVariants = cva(
  "inline-flex items-center justify-center w-fit whitespace-nowrap shrink-0 relative overflow-hidden inset-border-double",
  {
    variants: {
      variant: {
        pink: "text-accent-vivid-pink bg-accent-vivid-pink/10",
        skyblue: "text-accent-mute-skyblue bg-accent-mute-skyblue/10",
        green: "text-accent-mute-green bg-accent-mute-green/10",
        none: "text-labels-alternative bg-labels-alternative/10",
        // 상태가 아닌 값(회사명 등)을 담는 회색 칩
        secondary: "text-labels-secondary bg-labels-secondary/10",
        // 상태색을 뱃지에 입히지 않고 안쪽 요소(램프 등)에 맡기는 중립 칩
        neutral: "text-labels-secondary bg-fills-neutral",
        positive: "text-status-positive bg-status-positive/10",
        caution: "text-status-caution bg-status-caution/10",
        urgent: "text-status-urgent bg-status-urgent/10",

        yellow: "text-accent-mute-yellow bg-accent-vivid-yellow/10",
        red: "text-accent-mute-red bg-status-urgent/10", // noti ! : accent-vivid-yellow는 정의 되어 있지 않아 status-urgent로 사용 26.06.15
        lime: "text-accent-mute-lime bg-accent-vivid-lime/10",
        violet: "text-accent-mute-violet bg-accent-vivid-violet/10",
        purple: "text-accent-mute-purple bg-accent-vivid-purple/10",
        available: "text-status-available bg-status-available/10",
        primary: "text-primary-normal bg-primary-normal/10",
      },
      size: {
        sm: "typo-caption-2-semibold rounded-xs px-1 py-0.5",
        md: "typo-caption-1-semibold rounded-xs px-1.5 py-[3px]",
        lg: "typo-label-1-semibold rounded-md px-2 py-1",
      },
    },
    defaultVariants: {
      variant: "skyblue",
      size: "md",
    },
  },
);

function Badge({
  className,
  variant,
  size,
  asChild = false,
  ...props
}: React.ComponentProps<"span"> &
  VariantProps<typeof badgeVariants> & { asChild?: boolean }) {
  const Comp = asChild ? Slot : "span";

  return (
    <Comp
      data-slot="badge"
      data-variant={variant}
      data-size={size}
      className={cn(badgeVariants({ variant, size }), className)}
      {...props}
    />
  );
}

export { Badge, badgeVariants };
